//************************************************************************ /
// EVENT CARD
// Displays information for an upcoming event.
// User can "like" and "view stories" related to it.
//************************************************************************ /

import React, { useState } from "react";
import {
  Card,
  Divider,
  DecoratedImage,
} from "@dreambigger/design-system/src/components";
import {
  CardActions,
  CardDetails,
} from "@dreambigger/shared/src/components/board/feed-cards/card-components";
import {
  EventCommunityFeedItem,
  Image,
  Event,
  User,
} from "@dreambigger/shared/src/types";
import {
  generateCloudinaryUrl,
  optimizeCloudinaryImg,
} from "@dreambigger/shared/src/utils";
import anim from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { useRouter } from "next/router";
import { useMixpanel, useSegment } from "@dreambigger/shared/src/hooks";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { useGA } from "@dreambigger/shared/src/hooks";

type EventCardProps = {
  feedItem: EventCommunityFeedItem | Event;
  currentUser?: User;
};

const EventCard = ({ feedItem, currentUser }: EventCardProps) => {
  const {
    id,
    allDay,
    storyCount,
    images,
    name = "",
    host = "",
    startAt = "",
    endAt = "",
    likes,
    iLiked = false,
    location,
    pinned,
    timeZone,
  } = feedItem;

  // Return if no timezone is entered - All times will be wrong
  if (!timeZone) {
    return <></>;
  }
  const router = useRouter();

  // Load analytics
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  const eventUrl = `/events/${id}/detail`;

  const handleEventClick = () => {
    window.location.pathname = eventUrl;
  };

  // Refers to banner that displays on <DecoratedImage> - "HAPPENING NOW!". Enabled/disabled in <CardDetails>
  const [bannerEnabled, setBannerEnabled] = useState(false);

  const getCardImage = (images?: Image[]) => {
    if (!images?.[0]) {
      return "";
    }
    if (images[0].meta?.src) {
      return optimizeCloudinaryImg(images[0].meta.src);
    }
    if (!images[0].imageVersion) {
      return "";
    }
    return generateCloudinaryUrl(images[0].imageVersion, images[0].imageId);
  };

  const handleCardClick = () => {
    // Handle the analytics event where a user clicks the card.
    googleAnalytics.event({
      category: "Card Selection",
      action: "CTA Text:View Event",
      label: "Card Type:Event",
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.CARD_SELECTED,
      properties: {
        "Card Type": "Event",
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });
  };

  return (
    <Card
      onClick={handleCardClick}
      bodyStyle={{ padding: 0 }}
      cover={
        images && (
          <DecoratedImage
            alt="Event Image"
            src={getCardImage(images)}
            titleClass="white fwb f-5"
            bannerText="Happening Now!"
            bannerEnabled={bannerEnabled}
            onClick={handleEventClick}
            pinned={pinned}
            loading="lazy"
          />
        )
      }
      className={`${anim.enter_f} lift mh-auto bg-white pointer`}
    >
      {/* CONTENT ------------------------------------------------ */}
      <div onClick={handleEventClick} className="ph-5 pv-4">
        <CardDetails
          allDay={allDay}
          title={name || ""}
          host={host || ""}
          location={location.name || ""}
          startAt={startAt || ""}
          endAt={endAt || ""}
          font={"proxima-nova"}
          timeZone={timeZone}
          setBannerEnabled={setBannerEnabled}
        />
      </div>
      <Divider />

      {/* CARD ACTIONS BAR -------------------------------------- */}
      <CardActions
        id={id}
        user={currentUser}
        cardType="event"
        likeEnabled={true}
        storiesEnabled={true}
        userHasLiked={iLiked}
        likes={likes}
        numOfStories={storyCount}
        ctaTitle={"VIEW EVENT"}
        ctaLink={eventUrl}
      />
    </Card>
  );
};

export default EventCard;
